import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { PLAN_CATALOG } from './planConfig';
import EazyLogo from './EazyLogo';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/landing';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface LandingPageProps {
  onLogin: (user: any, token: string) => void;
}

function useInView(delay = 0): [React.MutableRefObject<any>, boolean] {
  const ref = useRef<any>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const show = () => setTimeout(() => setVisible(true), delay);
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) { show(); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { show(); obs.disconnect(); } },
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return [ref, visible];
}

const dark = {
  heroBg: 'linear-gradient(135deg, #0a0a1e 0%, #1a1040 50%, #0d1b3e 100%)',
  sectionBg: '#0a0a1e', sectionAlt: '#0d0d20', sectionDeep: '#070710',
  statsBg: '#0f0f23',
  nav: 'rgba(10,10,30,0.95)', navBorder: 'rgba(255,255,255,0.08)',
  text: 'white', textMuted: 'rgba(255,255,255,0.55)', textFaint: 'rgba(255,255,255,0.35)',
  textLabel: 'rgba(255,255,255,0.6)', textNav: 'rgba(255,255,255,0.7)',
  card: 'rgba(255,255,255,0.03)', cardHover: 'rgba(79,70,229,0.4)',
  border: 'rgba(255,255,255,0.08)', divider: 'rgba(255,255,255,0.06)',
  inputBg: 'rgba(255,255,255,0.06)', inputBorder: 'rgba(255,255,255,0.1)',
  ctaCard: 'rgba(79,70,229,0.08)', ctaBorder: 'rgba(79,70,229,0.2)',
  planCard: 'rgba(255,255,255,0.03)', planBorder: 'rgba(255,255,255,0.08)',
  planCardHL: 'rgba(79,70,229,0.12)', planBorderHL: 'rgba(79,70,229,0.5)',
  planBtn: 'rgba(255,255,255,0.08)', planBtnBorder: 'rgba(255,255,255,0.15)',
  statValue: '#818cf8', accent: '#818cf8', accentLabel: '#818cf8',
  checkmark: '#818cf8', stepNum: 'rgba(79,70,229,0.3)',
  glow1: 'radial-gradient(circle, rgba(79,70,229,0.15) 0%, transparent 70%)',
  glow2: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
  modalBg: '#111827', modalBorder: 'rgba(255,255,255,0.1)',
};

const light = {
  heroBg: '#f0ece5',
  sectionBg: '#f0ece5', sectionAlt: '#ebe7e0', sectionDeep: '#e6e2db',
  statsBg: '#ebe7e0',
  nav: 'rgba(240,236,229,0.97)', navBorder: '#d9d4cc',
  text: '#1a1a2e', textMuted: '#5c5750', textFaint: '#8c8680',
  textLabel: '#4a4540', textNav: '#2e2b27',
  card: '#f8f5f0', cardHover: 'rgba(79,70,229,0.3)',
  border: '#d9d4cc', divider: '#d9d4cc',
  inputBg: '#f8f5f0', inputBorder: '#c9c4bc',
  ctaCard: '#ede9fe', ctaBorder: 'rgba(79,70,229,0.2)',
  planCard: '#f8f5f0', planBorder: '#d9d4cc',
  planCardHL: '#ede9fe', planBorderHL: 'rgba(79,70,229,0.5)',
  planBtn: '#ebe7e0', planBtnBorder: '#d9d4cc',
  statValue: '#4f46e5', accent: '#4f46e5', accentLabel: '#4f46e5',
  checkmark: '#4f46e5', stepNum: 'rgba(79,70,229,0.2)',
  glow1: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 70%)',
  glow2: 'radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 70%)',
  modalBg: '#f8f5f0', modalBorder: '#d9d4cc',
};

const LandingPage: React.FC<LandingPageProps> = ({ onLogin }) => {
  const [isDark, setIsDark] = useState(() => localStorage.getItem('eazy-theme') !== 'light');
  const t = isDark ? dark : light;
  const { language, setLanguage } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [showLogin, setShowLogin] = useState(false);
  const [loginView, setLoginView] = useState<'login' | 'forgot' | 'forgot-success'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [stripePrices, setStripePrices] = useState<Record<string, number | null>>({});
  const [dbPlanLimits, setDbPlanLimits] = useState<Record<string, { maxProjects: number | null; maxUsers: number | null }>>({});
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [showPricingForm, setShowPricingForm] = useState<string | null>(null);
  const [pricingEmail, setPricingEmail] = useState('');
  const [pricingOrgName, setPricingOrgName] = useState('');
  const [faqOpen, setFaqOpen] = useState<Record<number, boolean>>({});
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialEmail, setTrialEmail] = useState('');
  const [trialOrgName, setTrialOrgName] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState('');
  const [trialSuccess, setTrialSuccess] = useState(false);
  const [activeDemo, setActiveDemo] = useState(0);
  const [featRef, featVisible] = useInView();
  const [compRef, compVisible] = useInView();

  useEffect(() => {
    const timer = setInterval(() => setActiveDemo(p => (p + 1) % 4), 4500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    axios.get(`${API}/payments/prices`).then(r => setStripePrices(r.data)).catch(() => {});
    axios.get(`${API}/payments/plan-limits`).then(r => setDbPlanLimits(r.data)).catch(() => {});
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('eazy-theme', next ? 'dark' : 'light');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setLoginLoading(true); setLoginError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(res.data.user, res.data.access_token);
    } catch { setLoginError(L.loginModal.error); }
    finally { setLoginLoading(false); }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault(); setLoginLoading(true); setLoginError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setLoginView('forgot-success');
    } catch { setLoginError(L.forgotModal.error); }
    finally { setLoginLoading(false); }
  };

  const closeModal = () => { setShowLogin(false); setLoginView('login'); setLoginError(''); setForgotEmail(''); };

  const handleStartTrial = async (e: React.FormEvent) => {
    e.preventDefault();
    setTrialLoading(true); setTrialError('');
    try {
      await axios.post(`${API}/auth/start-trial`, { email: trialEmail.trim(), orgName: trialOrgName.trim() });
      setTrialSuccess(true);
    } catch (err: any) {
      setTrialError(err?.response?.data?.message || L.trialModal.error);
    } finally {
      setTrialLoading(false);
    }
  };

  const handleCheckout = async (plan: string) => {
    if (!pricingEmail || !pricingOrgName) return;
    setCheckoutLoading(plan);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { plan, orgName: pricingOrgName, adminEmail: pricingEmail });
      window.location.href = res.data.url;
    } catch { alert(L.checkoutError); }
    finally { setCheckoutLoading(null); }
  };

  const plans = PLAN_CATALOG.map(p => {
    const dbLimits = dbPlanLimits[p.id];
    const maxProjects = dbLimits ? dbLimits.maxProjects : p.maxProjects;
    const maxUsers = dbLimits ? dbLimits.maxUsers : p.maxUsers;
    const planI18n = L.planCatalog[p.id as keyof typeof L.planCatalog];
    const dynamicFeatures = [
      maxProjects === null ? L.planFeatures.unlimitedProjects : L.planFeatures.maxProjects(maxProjects!),
      maxUsers === null ? L.planFeatures.unlimitedUsers : L.planFeatures.maxUsers(maxUsers!),
      ...(planI18n?.features ?? p.features.slice(2)),
    ];
    return {
      key: p.id, name: p.name,
      price: stripePrices[p.id] != null ? stripePrices[p.id]! : parseInt(p.priceNumber),
      desc: planI18n?.desc ?? p.desc, features: dynamicFeatures, highlight: p.highlighted,
    };
  });

  const faqs = L.faq.items;
  const features = L.features.items;
  const steps = L.how.steps;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', backgroundColor: t.inputBg,
    border: `1px solid ${t.inputBorder}`, borderRadius: '10px', fontSize: '14px',
    color: t.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = { display: 'block', color: t.textLabel, fontSize: '13px', fontWeight: 500, marginBottom: '8px' };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: t.text, overflowX: 'hidden', transition: 'background 0.3s' }}>
      <style>{`
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
        @keyframes demoProgress { from { width: 0%; } to { width: 100%; } }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: t.nav, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${t.navBorder}`, padding: isMobile ? '0 16px' : '0 40px', height: '64px', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
        <EazyLogo size="md" theme={isDark ? 'dark' : 'light'} />

        {/* Center: theme toggle */}
        <button
          onClick={toggleTheme}
          title={isDark ? L.themeToggle.toLight : L.themeToggle.toDark}
          style={{
            width: 40, height: 40, borderRadius: '50%',
            border: `1px solid ${t.border}`,
            backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(79,70,229,0.08)',
            cursor: 'pointer', fontSize: 20,
            color: isDark ? '#818cf8' : '#4f46e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s, border-color 0.2s',
          }}
        >
          {isDark ? '☽' : '☀'}
        </button>

        {/* Right: nav links + login + lang */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '28px', justifyContent: 'flex-end' }}>
          {!isMobile && <a href="#features" style={{ color: t.textNav, textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>Features</a>}
          {!isMobile && <a href="#how" style={{ color: t.textNav, textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>{L.nav.how}</a>}
          {!isMobile && <a href="#pricing" style={{ color: t.textNav, textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>{L.nav.pricing}</a>}
          <button onClick={() => setShowLogin(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: isMobile ? '8px 14px' : '9px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
            {L.nav.login}
          </button>

          {/* Language switcher */}
          <div ref={langRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowLangMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderRadius: '8px', border: `1px solid ${t.border}`, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(79,70,229,0.06)', color: t.textNav, fontSize: '13px', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.5px' }}
            >
              {language === 'en' ? 'EN' : 'GR'}
            </button>
            {showLangMenu && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, backgroundColor: isDark ? '#1e1e35' : 'white', border: `1px solid ${t.border}`, borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', overflow: 'hidden', minWidth: '140px', zIndex: 200 }}>
                {(['el', 'en'] as const).map(lang => (
                  <button
                    key={lang}
                    onClick={() => { setLanguage(lang); setShowLangMenu(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: language === lang ? 600 : 400, color: language === lang ? '#4f46e5' : t.textNav, textAlign: 'left' }}
                  >
                    {lang === 'el' ? 'Ελληνικά' : 'English'}
                    {language === lang && <span style={{ color: '#4f46e5', fontSize: '13px' }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ minHeight: '100vh', background: t.heroBg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '72px 20px 8px' : '72px 60px 8px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '20%', left: '10%', width: '400px', height: '400px', background: t.glow1, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '10%', width: '300px', height: '300px', background: t.glow2, pointerEvents: 'none' }} />
        <div style={{ maxWidth: '1200px', width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '48px' : '72px', alignItems: 'center', position: 'relative', zIndex: 1 }}>

          {/* Left: text */}
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.25)', borderRadius: '20px', padding: '6px 16px', marginBottom: '28px' }}>
              <span style={{ fontSize: '12px', color: t.accent, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>AI-Powered Project Management</span>
            </div>
            <h1 style={{ fontSize: isMobile ? '36px' : '54px', fontWeight: 800, color: t.text, lineHeight: 1.15, letterSpacing: isMobile ? '-1px' : '-2px', margin: '0 0 20px' }}>
              {L.hero.title1}<br />
              <span style={{ background: 'linear-gradient(90deg, #818cf8, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{L.hero.title2}</span>
            </h1>
            <p style={{ fontSize: isMobile ? '16px' : '17px', color: t.textMuted, lineHeight: 1.7, maxWidth: '460px', margin: isMobile ? '0 auto 36px' : '0 0 36px' }}>
              {L.hero.subtitle}
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>
              <button onClick={() => setShowLogin(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '14px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 40px rgba(79,70,229,0.35)', width: isMobile ? '100%' : 'auto' }}>
                {L.hero.cta}
              </button>
              <a href="#how" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(79,70,229,0.08)', color: t.text, border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(79,70,229,0.2)'}`, padding: '14px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: 600, textDecoration: 'none', display: 'inline-block', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' as const }}>
                {L.hero.learn}
              </a>
            </div>
          </div>

          {/* Right: Animated app demo */}
          {!isMobile && (() => {
            const TABS = [
              { label: 'Schedule', icon: '▦' },
              { label: 'Budget', icon: '◈' },
              { label: 'AI Advisor', icon: '✦' },
              { label: 'Risks', icon: '⚑' },
            ];
            return (
              <div style={{ background: '#080818', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)', transform: 'perspective(1200px) rotateY(-5deg) rotateX(2deg)', display: 'flex', flexDirection: 'column' }}>
                {/* Chrome bar */}
                <div style={{ background: '#0e0e22', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block' }} />
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                  <span style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.03)', padding: '2px 0', borderRadius: 4, margin: '0 20px' }}>eazyproject.cloud</span>
                </div>
                {/* App shell */}
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  {/* Sidebar */}
                  <div style={{ width: 44, background: '#0a0a1f', borderRight: '1px solid rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: 4, flexShrink: 0 }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'linear-gradient(135deg,#4f46e5,#818cf8)', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: '#fff' }}>E</div>
                    {TABS.map((t2, i) => (
                      <button key={i} onClick={() => setActiveDemo(i)} title={t2.label} style={{ width: 30, height: 30, borderRadius: 7, background: activeDemo === i ? 'rgba(79,70,229,0.25)' : 'transparent', border: activeDemo === i ? '1px solid rgba(79,70,229,0.4)' : '1px solid transparent', color: activeDemo === i ? '#818cf8' : 'rgba(255,255,255,0.2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                        {t2.icon}
                      </button>
                    ))}
                    <div style={{ flex: 1 }} />
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>JD</div>
                  </div>
                  {/* Main content */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* Top bar */}
                    <div style={{ background: '#0c0c20', borderBottom: '1px solid rgba(255,255,255,0.04)', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', flex: 1 }}>{TABS[activeDemo].label}</span>
                      <span style={{ fontSize: 9, color: '#22c55e', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>ON TRACK</span>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>Digital HQ · Phase 1</span>
                      {/* Progress bar */}
                      <div style={{ position: 'absolute', bottom: 0, left: 44, right: 0, height: 1.5, background: 'rgba(255,255,255,0.04)' }}>
                        <div key={activeDemo} style={{ height: '100%', background: 'linear-gradient(90deg,#4f46e5,#818cf8)', animation: 'demoProgress 4.5s linear forwards' }} />
                      </div>
                    </div>
                    {/* Module content */}
                    <div key={activeDemo} style={{ flex: 1, overflowY: 'auto', animation: 'fadeSlideUp 0.35s ease forwards' }}>

                      {/* ── SCHEDULE ── */}
                      {activeDemo === 0 && (
                        <div style={{ padding: '12px' }}>
                          {/* Summary row */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
                            {[
                              { label: 'Overall progress', value: '54%', color: '#818cf8' },
                              { label: 'Days remaining', value: '38', color: '#f0abfc' },
                              { label: 'Delayed tasks', value: '2', color: '#f87171' },
                            ].map((k, i) => (
                              <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 6, padding: '7px 9px' }}>
                                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{k.label}</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: k.color }}>{k.value}</div>
                              </div>
                            ))}
                          </div>
                          {/* Phase label */}
                          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, marginBottom: 8 }}>Phase 1 — Analysis & Design</div>
                          {/* Tasks */}
                          {[
                            { name: 'Feasibility Study', start: 'Jan 6', end: 'Jan 17', pct: 100, status: 'done' },
                            { name: 'Requirements Analysis', start: 'Jan 20', end: 'Feb 7', pct: 72, status: 'late' },
                            { name: 'System Architecture', start: 'Feb 10', end: 'Mar 7', pct: 40, status: 'ok' },
                            { name: 'Phase 1 Delivery', start: 'Mar 10', end: 'Mar 14', pct: 0, status: 'pending' },
                          ].map((row, i) => {
                            const barColor = row.status === 'done' ? '#4f46e5' : row.status === 'late' ? '#f87171' : row.status === 'ok' ? '#6366f1' : 'rgba(255,255,255,0.08)';
                            return (
                              <div key={i} style={{ marginBottom: 9, padding: '7px 8px', background: 'rgba(255,255,255,0.015)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.04)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'center' }}>
                                  <span style={{ fontSize: 10, color: row.status === 'pending' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.65)', fontWeight: 500 }}>{row.name}</span>
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)' }}>{row.start} → {row.end}</span>
                                    {row.status === 'late' && <span style={{ fontSize: 8, color: '#f87171', background: 'rgba(248,113,113,0.12)', padding: '1px 5px', borderRadius: 3, fontWeight: 700 }}>LATE</span>}
                                    {row.status === 'done' && <span style={{ fontSize: 8, color: '#22c55e', background: 'rgba(34,197,94,0.1)', padding: '1px 5px', borderRadius: 3, fontWeight: 700 }}>✓</span>}
                                    <span style={{ fontSize: 9, color: barColor, fontWeight: 700 }}>{row.pct}%</span>
                                  </div>
                                </div>
                                <div style={{ height: 4, background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
                                  <div style={{ height: '100%', width: `${row.pct}%`, background: barColor, borderRadius: 2, transition: 'width 0.5s ease' }} />
                                </div>
                              </div>
                            );
                          })}
                          {/* AI tip */}
                          <div style={{ marginTop: 10, background: 'rgba(79,70,229,0.07)', border: '1px solid rgba(79,70,229,0.2)', borderRadius: 8, padding: '9px 10px', display: 'flex', gap: 7 }}>
                            <span style={{ fontSize: 12, color: '#818cf8', flexShrink: 0 }}>✦</span>
                            <div>
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#818cf8' }}>AI Advisor · </span>
                              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.38)', lineHeight: 1.5 }}>"Requirements Analysis" delay risks Phase 1 delivery. Recommend reassigning 1 resource from Architecture.</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── BUDGET ── */}
                      {activeDemo === 1 && (
                        <div style={{ padding: '12px' }}>
                          {/* KPI cards */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, marginBottom: 12 }}>
                            {[
                              { label: 'Budget (BAC)', value: '€285K', sub: 'Approved', color: '#818cf8' },
                              { label: 'Actual Cost (AC)', value: '€142K', sub: '50% spent', color: '#6366f1' },
                              { label: 'Earned Value (EV)', value: '€128K', sub: 'CPI 0.90 ↓', color: '#f59e0b' },
                              { label: 'Forecast (EAC)', value: '€317K', sub: '+€32K overrun', color: '#f87171' },
                            ].map((k, i) => (
                              <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${i === 3 ? 'rgba(248,113,113,0.2)' : 'rgba(255,255,255,0.05)'}`, borderRadius: 8, padding: '9px 10px' }}>
                                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{k.label}</div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: k.color, marginBottom: 1 }}>{k.value}</div>
                                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.22)' }}>{k.sub}</div>
                              </div>
                            ))}
                          </div>
                          {/* Cost breakdown */}
                          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.22)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 7, fontWeight: 600 }}>Cost breakdown</div>
                          {[
                            { item: 'Human Resources', budget: '€180K', actual: '€95K', pct: 53, alert: false },
                            { item: 'Equipment & Licenses', budget: '€60K', actual: '€38K', pct: 63, alert: false },
                            { item: 'Subcontracting', budget: '€45K', actual: '€9K', pct: 20, alert: false },
                            { item: 'Contingency', budget: '€0K', actual: '€32K', pct: 100, alert: true },
                          ].map((row, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.8fr 0.8fr 52px', padding: '7px 0', borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.04)' : 'none', alignItems: 'center', gap: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {row.alert && <span style={{ fontSize: 8, color: '#f87171' }}>⚠</span>}
                                <span style={{ fontSize: 10, color: row.alert ? '#f87171' : 'rgba(255,255,255,0.5)' }}>{row.item}</span>
                              </div>
                              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', textAlign: 'right' }}>{row.budget}</span>
                              <span style={{ fontSize: 9, color: row.alert ? '#f87171' : '#818cf8', textAlign: 'right', fontWeight: 600 }}>{row.actual}</span>
                              <div style={{ height: 3, background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
                                <div style={{ height: '100%', width: `${Math.min(row.pct, 100)}%`, background: row.alert ? '#f87171' : '#4f46e5', borderRadius: 2 }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ── AI ADVISOR ── */}
                      {activeDemo === 2 && (
                        <div style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>Sources:</span>
                            {['Contract_v3.pdf', 'Meeting_Jun10.docx', '3 emails'].map((s, i) => (
                              <span key={i} style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4 }}>{s}</span>
                            ))}
                          </div>
                          {/* Insights */}
                          {[
                            { title: 'Supplier Delivery Delay', level: 'HIGH', color: '#ef4444', desc: 'Confirmed in email 10/6 — affects Phase 2 milestone by +12 working days.', action: 'Escalate to client' },
                            { title: 'Budget Overrun Risk', level: 'MEDIUM', color: '#f59e0b', desc: 'EAC €317K vs BAC €285K. CPI 0.90 trending downward for 3 weeks.', action: 'Revise EAC' },
                            { title: 'Scope Change Request', level: 'LOW', color: '#22c55e', desc: 'Client email 12/6 requests minor UI additions. No cost approved yet.', action: 'Raise change order' },
                            { title: 'Resource Bottleneck', level: 'MEDIUM', color: '#f59e0b', desc: 'Lead architect assigned to 3 tasks concurrently — flagged in last meeting.', action: 'Reassign tasks' },
                          ].map((r, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 9px', background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 7, marginBottom: 5, alignItems: 'flex-start' }}>
                              <span style={{ fontSize: 8, fontWeight: 700, color: r.color, background: `${r.color}18`, padding: '2px 5px', borderRadius: 3, whiteSpace: 'nowrap', marginTop: 1, border: `1px solid ${r.color}30` }}>{r.level}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>{r.title}</div>
                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.32)', lineHeight: 1.5, marginBottom: 4 }}>{r.desc}</div>
                                <span style={{ fontSize: 8, color: '#818cf8', background: 'rgba(79,70,229,0.12)', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>→ {r.action}</span>
                              </div>
                            </div>
                          ))}
                          {/* AI summary */}
                          <div style={{ marginTop: 8, background: 'rgba(79,70,229,0.07)', border: '1px solid rgba(79,70,229,0.18)', borderRadius: 8, padding: '8px 10px', display: 'flex', gap: 7 }}>
                            <span style={{ fontSize: 12, color: '#818cf8', flexShrink: 0 }}>✦</span>
                            <div>
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#818cf8' }}>AI Summary · </span>
                              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.36)', lineHeight: 1.5 }}>2 medium risks need attention this week. Supplier delay is the critical path item. Update EAC and schedule before client meeting.</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── RISKS ── */}
                      {activeDemo === 3 && (
                        <div style={{ padding: '12px' }}>
                          {/* Summary pills */}
                          <div style={{ display: 'flex', gap: 6, marginBottom: 11 }}>
                            {[{ label: '1 Critical', color: '#ef4444' }, { label: '2 High', color: '#f59e0b' }, { label: '3 Medium', color: '#6366f1' }, { label: '1 Low', color: '#22c55e' }].map((p, i) => (
                              <span key={i} style={{ fontSize: 9, fontWeight: 700, color: p.color, background: `${p.color}15`, border: `1px solid ${p.color}30`, padding: '3px 8px', borderRadius: 20 }}>{p.label}</span>
                            ))}
                          </div>
                          {/* Risk register */}
                          <div style={{ display: 'grid', gridTemplateColumns: '7px 1fr 40px 40px 46px 60px', gap: 0, fontSize: 8, color: 'rgba(255,255,255,0.2)', padding: '0 6px 5px', textTransform: 'uppercase', letterSpacing: 1 }}>
                            <span />
                            <span>Risk</span>
                            <span style={{ textAlign: 'center' }}>Prob</span>
                            <span style={{ textAlign: 'center' }}>Impact</span>
                            <span style={{ textAlign: 'center' }}>Score</span>
                            <span style={{ textAlign: 'center' }}>Status</span>
                          </div>
                          {[
                            { id: 'R-01', name: 'Key developer leaves project', prob: 'H', impact: 'H', score: 16, scoreColor: '#ef4444', status: 'Mitigating', statusColor: '#f59e0b', strip: '#ef4444' },
                            { id: 'R-02', name: 'Supplier delivery delay', prob: 'H', impact: 'H', score: 12, scoreColor: '#f59e0b', status: 'Escalated', statusColor: '#ef4444', strip: '#f59e0b' },
                            { id: 'R-03', name: 'Scope creep from client', prob: 'M', impact: 'H', score: 9, scoreColor: '#f59e0b', status: 'Monitoring', statusColor: '#818cf8', strip: '#f59e0b' },
                            { id: 'R-04', name: 'Integration test failures', prob: 'M', impact: 'M', score: 6, scoreColor: '#6366f1', status: 'Open', statusColor: '#6366f1', strip: '#6366f1' },
                            { id: 'R-05', name: 'Regulatory change (GDPR)', prob: 'L', impact: 'H', score: 4, scoreColor: '#22c55e', status: 'Accepted', statusColor: '#22c55e', strip: '#22c55e' },
                          ].map((r, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '7px 1fr 40px 40px 46px 60px', gap: 0, padding: '7px 6px', background: 'rgba(255,255,255,0.015)', borderRadius: 6, marginBottom: 3, alignItems: 'center', border: '1px solid rgba(255,255,255,0.04)' }}>
                              <div style={{ width: 3, height: 22, background: r.strip, borderRadius: 2, marginRight: 6 }} />
                              <div>
                                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', marginBottom: 1 }}>{r.id}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.62)', fontWeight: 500, lineHeight: 1.3 }}>{r.name}</div>
                              </div>
                              <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>{r.prob}</div>
                              <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>{r.impact}</div>
                              <div style={{ textAlign: 'center' }}>
                                <span style={{ fontSize: 11, fontWeight: 800, color: r.scoreColor }}>{r.score}</span>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <span style={{ fontSize: 8, fontWeight: 700, color: r.statusColor, background: `${r.statusColor}15`, border: `1px solid ${r.statusColor}25`, padding: '2px 5px', borderRadius: 3, whiteSpace: 'nowrap' }}>{r.status}</span>
                              </div>
                            </div>
                          ))}
                          {/* AI */}
                          <div style={{ marginTop: 8, background: 'rgba(79,70,229,0.07)', border: '1px solid rgba(79,70,229,0.18)', borderRadius: 8, padding: '8px 10px', display: 'flex', gap: 7 }}>
                            <span style={{ fontSize: 12, color: '#818cf8', flexShrink: 0 }}>✦</span>
                            <div>
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#818cf8' }}>AI Advisor · </span>
                              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.36)', lineHeight: 1.5 }}>R-01 and R-02 share the same critical path. Mitigating R-01 first reduces overall project exposure by 34%.</span>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div style={{ background: isDark ? '#0f0f23' : '#ebe7e0', borderTop: `1px solid ${t.divider}`, borderBottom: `1px solid ${t.divider}`, padding: isMobile ? '20px' : '18px 40px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)' }}>
          {[
            ...L.stats,
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '14px 12px', borderRight: i < 3 && !isMobile ? `1px solid ${t.divider}` : 'none', borderBottom: isMobile && i < 2 ? `1px solid ${t.divider}` : 'none' }}>
              <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 800, color: t.accent, marginBottom: 3 }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: t.textFaint, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURES ── */}
      <section id="features" style={{ backgroundColor: t.sectionBg, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <p style={{ color: t.accentLabel, fontWeight: 600, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>{L.features.label}</p>
            <h2 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: 0 }}>{L.features.title}</h2>
          </div>
          <div ref={featRef} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
            {features.map((f, i) => (
              <div key={i} style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, borderRadius: '16px', padding: '32px', transition: `border-color 0.2s, opacity 0.6s ${i * 0.1}s ease, transform 0.6s ${i * 0.1}s ease`, opacity: featVisible ? 1 : 0, transform: featVisible ? 'none' : 'translateY(20px)' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = t.cardHover)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = t.border)}>
                <div style={{ width: '44px', height: '44px', backgroundColor: 'rgba(79,70,229,0.12)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#818cf8', marginBottom: '18px' }}>
                  {f.icon}
                </div>
                <h3 style={{ fontSize: '19px', fontWeight: 700, color: t.text, margin: '0 0 10px' }}>{f.title}</h3>
                <p style={{ fontSize: '14px', color: t.textMuted, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section style={{ backgroundColor: t.sectionAlt, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '56px' }}>
            <p style={{ color: t.accentLabel, fontWeight: 600, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>{L.comparison.label}</p>
            <h2 style={{ fontSize: isMobile ? '28px' : '38px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 12px' }}>{L.comparison.title}</h2>
            <p style={{ fontSize: '15px', color: t.textMuted, margin: 0 }}>{L.comparison.subtitle}</p>
          </div>
          <div ref={compRef} style={{ border: `1px solid ${t.border}`, borderRadius: '16px', overflow: 'hidden', transition: 'opacity 0.7s ease, transform 0.7s ease', opacity: compVisible ? 1 : 0, transform: compVisible ? 'none' : 'translateY(24px)' }}>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(79,70,229,0.04)', padding: '14px 20px', borderBottom: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Feature</span>
              {['eazyproject', 'Asana', 'Monday', 'Trello'].map((h, i) => (
                <span key={h} style={{ fontSize: 12, fontWeight: 700, color: i === 0 ? '#818cf8' : t.textFaint, textAlign: 'center' }}>{h}</span>
              ))}
            </div>
            {/* Data rows */}
            {[
              { feature: L.comparison.features[0], vals: ['✓', '~', '~', '✗'] },
              { feature: L.comparison.features[1], vals: ['✓', '✗', '✗', '✗'] },
              { feature: L.comparison.features[2], vals: ['✓', '✗', '~', '✗'] },
              { feature: L.comparison.features[3], vals: ['✓', '✓', '✓', '~'] },
              { feature: L.comparison.features[4], vals: ['✓', '✗', '✗', '✗'] },
            ].map((row, i, arr) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '13px 20px', borderBottom: i < arr.length - 1 ? `1px solid ${t.divider}` : 'none', background: i % 2 !== 0 ? (isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                <span style={{ fontSize: 13, color: t.textMuted }}>{row.feature}</span>
                {row.vals.map((v, j) => (
                  <span key={j} style={{ textAlign: 'center', fontSize: 15, color: j === 0 ? '#4ade80' : v === '✓' ? t.textMuted : v === '~' ? '#fbbf24' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)'), fontWeight: j === 0 ? 700 : 400 }}>
                    {v}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Mini CTA */}
          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <button
              onClick={() => { setShowTrialModal(true); setTrialEmail(''); setTrialOrgName(''); setTrialError(''); setTrialSuccess(false); }}
              style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '13px 32px', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 30px rgba(79,70,229,0.3)' }}
            >
              {L.trial.cta}
            </button>
            <p style={{ fontSize: '12px', color: t.textFaint, margin: '10px 0 0' }}>{L.comparison.miniCtaNote}</p>
          </div>
        </div>
      </section>


      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ backgroundColor: t.sectionAlt, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <p style={{ color: t.accentLabel, fontWeight: 600, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>{L.how.label}</p>
            <h2 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: 0 }}>{L.how.title}</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', padding: '40px 0', borderBottom: i < steps.length - 1 ? `1px solid ${t.divider}` : 'none' }}>
                <div style={{ fontSize: '44px', fontWeight: 800, color: t.stepNum, lineHeight: 1, minWidth: '56px' }}>{s.num}</div>
                <div>
                  <h3 style={{ fontSize: '21px', fontWeight: 700, color: t.text, margin: '0 0 10px' }}>{s.title}</h3>
                  <p style={{ fontSize: '15px', color: t.textMuted, lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ backgroundColor: t.sectionBg, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
            <p style={{ color: t.accentLabel, fontWeight: 600, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>{L.pricing.label}</p>
            <h2 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 14px' }}>{L.pricing.title}</h2>
            <p style={{ color: t.textMuted, fontSize: '16px', margin: 0 }}>{L.pricing.subtitle}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px', alignItems: 'start' }}>
            {plans.map(plan => (
              <div key={plan.key} style={{ backgroundColor: plan.highlight ? t.planCardHL : t.planCard, border: `1px solid ${plan.highlight ? t.planBorderHL : t.planBorder}`, borderRadius: '20px', padding: '36px 28px', position: 'relative' }}>
                {plan.highlight && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4f46e5', color: 'white', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {L.pricing.popular}
                  </div>
                )}
                <h3 style={{ color: t.text, fontSize: '19px', fontWeight: 700, margin: '0 0 4px' }}>{plan.name}</h3>
                <p style={{ color: t.textMuted, fontSize: '13px', margin: '0 0 20px' }}>{plan.desc}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '24px' }}>
                  <span style={{ color: t.text, fontSize: '46px', fontWeight: 800, letterSpacing: '-2px' }}>€{plan.price}</span>
                  <span style={{ color: t.textFaint, fontSize: '14px' }}>{L.pricing.perMonth}</span>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px' }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: `1px solid ${t.divider}`, color: t.textMuted, fontSize: '14px' }}>
                      <span style={{ color: t.checkmark, fontSize: '15px', fontWeight: 700 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => { setShowPricingForm(plan.key); setPricingEmail(''); setPricingOrgName(''); }}
                  style={{ width: '100%', padding: '12px', backgroundColor: plan.highlight ? '#4f46e5' : t.planBtn, color: plan.highlight ? 'white' : t.text, border: plan.highlight ? 'none' : `1px solid ${t.planBtnBorder}`, borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: plan.highlight ? '0 0 20px rgba(79,70,229,0.3)' : 'none' }}>
                  {L.pricing.cta(plan.name)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FREE TRIAL STRIP ── */}
      <div style={{ backgroundColor: t.sectionBg, padding: isMobile ? '0 20px 40px' : '0 40px 56px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            background: isDark
              ? 'linear-gradient(135deg, rgba(79,70,229,0.1) 0%, rgba(139,92,246,0.08) 100%)'
              : 'linear-gradient(135deg, rgba(79,70,229,0.06) 0%, rgba(139,92,246,0.04) 100%)',
            border: `1px solid ${isDark ? 'rgba(79,70,229,0.25)' : 'rgba(79,70,229,0.18)'}`,
            borderRadius: '18px', padding: isMobile ? '24px 20px' : '28px 36px',
            display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 20,
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 20, color: '#818cf8' }}>✦</span>
                <span style={{ fontWeight: 700, fontSize: 17, color: t.text }}>{L.trial.title}</span>
              </div>
              <p style={{ color: t.textMuted, fontSize: 13, margin: 0 }}>{L.trial.subtitle}</p>
            </div>
            <button
              onClick={() => { setShowTrialModal(true); setTrialEmail(''); setTrialOrgName(''); setTrialError(''); setTrialSuccess(false); }}
              style={{
                backgroundColor: '#4f46e5', color: 'white', border: 'none',
                padding: '11px 26px', borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                boxShadow: '0 0 20px rgba(79,70,229,0.25)',
              }}
            >
              {L.trial.cta}
            </button>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <section style={{ backgroundColor: t.sectionAlt, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '740px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '56px' }}>
            <p style={{ color: t.accentLabel, fontWeight: 600, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>{L.faq.label}</p>
            <h2 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: 0 }}>{L.faq.title}</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${t.divider}` }}>
                <button
                  onClick={() => setFaqOpen(prev => ({ ...prev, [i]: !prev[i] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '22px 0', background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', gap: 16,
                  }}>
                  <span style={{ fontSize: '16px', fontWeight: 600, color: t.text, lineHeight: 1.4 }}>{faq.q}</span>
                  <span style={{
                    fontSize: '20px', color: t.accent, flexShrink: 0, lineHeight: 1,
                    transition: 'transform 0.25s',
                    transform: faqOpen[i] ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}>+</span>
                </button>
                <div style={{
                  overflow: 'hidden',
                  maxHeight: faqOpen[i] ? '200px' : '0px',
                  transition: 'max-height 0.3s ease',
                }}>
                  <p style={{ fontSize: '15px', color: t.textMuted, lineHeight: 1.7, margin: '0 0 22px', paddingRight: 32 }}>
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ backgroundColor: t.sectionAlt, padding: isMobile ? '60px 20px' : '100px 40px', borderTop: `1px solid ${t.divider}` }}>
        <div style={{ maxWidth: '680px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ backgroundColor: t.ctaCard, border: `1px solid ${t.ctaBorder}`, borderRadius: '24px', padding: isMobile ? '36px 24px' : '60px 48px' }}>
            <h2 style={{ fontSize: isMobile ? '26px' : '38px', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 14px' }}>{L.cta.title}</h2>
            <p style={{ fontSize: '16px', color: t.textMuted, lineHeight: 1.7, margin: '0 0 36px' }}>{L.cta.subtitle}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
              <button onClick={() => setShowLogin(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '13px 32px', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 30px rgba(79,70,229,0.3)' }}>
                {L.cta.login}
              </button>
              <a href="mailto:info@eazyproject.cloud" style={{ backgroundColor: 'transparent', color: t.textMuted, border: `1px solid ${t.border}`, padding: '13px 32px', borderRadius: '10px', fontSize: '15px', fontWeight: 600, textDecoration: 'none' }}>
                {L.cta.contact}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: t.sectionDeep, borderTop: `1px solid ${t.divider}`, padding: isMobile ? '36px 20px' : '36px 40px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
          <EazyLogo size="sm" theme={isDark ? 'dark' : 'light'} />
        </div>
        <p style={{ color: t.textFaint, fontSize: '13px', margin: '0 0 12px' }}>
          © {new Date().getFullYear()} eazyproject · AI-Powered Project Management
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: t.textFaint, fontSize: '12px', textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = t.accent)}
            onMouseLeave={e => (e.currentTarget.style.color = t.textFaint)}>
            Privacy Policy
          </a>
          <span style={{ color: t.divider }}>·</span>
          <a href="/cookies" target="_blank" rel="noreferrer" style={{ color: t.textFaint, fontSize: '12px', textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = t.accent)}
            onMouseLeave={e => (e.currentTarget.style.color = t.textFaint)}>
            Cookie Policy
          </a>
        </div>
      </footer>

      {/* ── PRICING FORM MODAL ── */}
      {showPricingForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(6px)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowPricingForm(null); }}>
          <div style={{ backgroundColor: t.modalBg, border: `1px solid ${t.modalBorder}`, borderRadius: '20px', padding: isMobile ? '28px 20px' : '40px', width: 'min(420px, 90vw)', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ color: t.text, fontSize: '22px', fontWeight: 700, margin: '0 0 6px' }}>{L.pricingModal.title(plans.find(p => p.key === showPricingForm)?.name ?? '')}</h2>
            <p style={{ color: t.textMuted, fontSize: '14px', margin: '0 0 28px' }}>{L.pricingModal.subtitle}</p>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{L.pricingModal.orgLabel}</label>
              <input value={pricingOrgName} onChange={e => setPricingOrgName(e.target.value)} placeholder={L.pricingModal.orgNamePlaceholder} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>{L.pricingModal.emailLabel}</label>
              <input type="email" value={pricingEmail} onChange={e => setPricingEmail(e.target.value)} placeholder="admin@company.com" style={inputStyle} />
            </div>
            <p style={{ color: t.textFaint, fontSize: '12px', margin: '0 0 20px' }}>{L.pricingModal.note}</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowPricingForm(null)} style={{ flex: 1, padding: '12px', backgroundColor: 'transparent', color: t.textMuted, border: `1px solid ${t.border}`, borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>{L.pricingModal.cancel}</button>
              <button onClick={() => handleCheckout(showPricingForm)} disabled={!pricingEmail || !pricingOrgName || checkoutLoading === showPricingForm}
                style={{ flex: 2, padding: '12px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: (!pricingEmail || !pricingOrgName) ? 0.5 : 1 }}>
                {checkoutLoading === showPricingForm ? L.pricingModal.submitting : L.pricingModal.submit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOGIN MODAL ── */}
      {showLogin && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(6px)' }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{ backgroundColor: t.modalBg, border: `1px solid ${t.modalBorder}`, borderRadius: '20px', padding: isMobile ? '28px 20px' : '40px', width: 'min(400px, 90vw)', boxShadow: '0 25px 60px rgba(0,0,0,0.4)', position: 'relative' }}>
            <button onClick={closeModal} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: t.textFaint, lineHeight: 1 }}>✕</button>
            <div style={{ marginBottom: '28px' }}><EazyLogo size="lg" theme={isDark ? 'dark' : 'light'} /></div>

            {loginView === 'login' && (
              <>
                <h2 style={{ color: t.text, fontSize: '22px', fontWeight: 700, margin: '0 0 6px' }}>{L.loginModal.welcome}</h2>
                <p style={{ color: t.textMuted, fontSize: '14px', margin: '0 0 28px' }}>{L.loginModal.subtitle}</p>
                <form onSubmit={handleLogin}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#4f46e5'} onBlur={e => e.target.style.borderColor = t.inputBorder} />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={labelStyle}>{L.loginModal.password}</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle}
                        onFocus={e => e.target.style.borderColor = '#4f46e5'} onBlur={e => e.target.style.borderColor = t.inputBorder} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: t.textFaint, fontSize: '12px', fontWeight: 500 }}>
                        {showPassword ? L.loginModal.hide : L.loginModal.show}
                      </button>
                    </div>
                  </div>
                  {loginError && <p style={{ color: '#f87171', fontSize: '13px', margin: '8px 0 0', backgroundColor: 'rgba(239,68,68,0.1)', padding: '8px 12px', borderRadius: '8px' }}>{loginError}</p>}
                  <button type="submit" disabled={loginLoading} style={{ width: '100%', padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', marginTop: '20px', boxShadow: '0 0 20px rgba(79,70,229,0.3)' }}>
                    {loginLoading ? L.loginModal.submitting : L.loginModal.submit}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button type="button" onClick={() => { setLoginView('forgot'); setLoginError(''); }} style={{ background: 'none', border: 'none', color: t.textFaint, fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}>
                      {L.loginModal.forgot}
                    </button>
                  </div>
                </form>
              </>
            )}

            {loginView === 'forgot' && (
              <>
                <h2 style={{ color: t.text, fontSize: '22px', fontWeight: 700, margin: '0 0 6px' }}>{L.forgotModal.title}</h2>
                <p style={{ color: t.textMuted, fontSize: '14px', margin: '0 0 24px' }}>{L.forgotModal.subtitle}</p>
                <div style={{ backgroundColor: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: 'rgba(125,211,252,0.9)' }}>{L.forgotModal.info}</p>
                </div>
                <form onSubmit={handleForgot}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>{L.forgotModal.emailLabel}</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" required autoFocus style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#4f46e5'} onBlur={e => e.target.style.borderColor = t.inputBorder} />
                  </div>
                  {loginError && <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 8px', backgroundColor: 'rgba(239,68,68,0.1)', padding: '8px 12px', borderRadius: '8px' }}>{loginError}</p>}
                  <button type="submit" disabled={loginLoading || !forgotEmail.trim()} style={{ width: '100%', padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', marginTop: '4px', opacity: !forgotEmail.trim() ? 0.5 : 1 }}>
                    {loginLoading ? L.forgotModal.submitting : L.forgotModal.submit}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button type="button" onClick={() => { setLoginView('login'); setLoginError(''); }} style={{ background: 'none', border: 'none', color: t.textFaint, fontSize: '13px', cursor: 'pointer' }}>
                      {L.forgotModal.back}
                    </button>
                  </div>
                </form>
              </>
            )}

            {loginView === 'forgot-success' && (
              <div style={{ textAlign: 'center', paddingTop: '8px' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '24px' }}>✉</div>
                <h3 style={{ color: t.text, fontSize: '20px', fontWeight: 700, margin: '0 0 10px' }}>{L.forgotSuccess.title}</h3>
                <p style={{ color: t.textMuted, fontSize: '14px', margin: '0 0 8px', lineHeight: 1.5 }}>
                  {L.forgotSuccess.body(forgotEmail)}
                </p>
                <p style={{ color: t.textFaint, fontSize: '12px', margin: '0 0 28px' }}>{L.forgotSuccess.spam}</p>
                <button onClick={closeModal} style={{ width: '100%', padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
                  {L.forgotSuccess.back}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TRIAL MODAL ── */}
      {showTrialModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(6px)' }}
          onClick={e => { if (e.target === e.currentTarget) { setShowTrialModal(false); setTrialSuccess(false); } }}>
          <div style={{ backgroundColor: t.modalBg, border: `1px solid ${t.modalBorder}`, borderRadius: '20px', padding: isMobile ? '28px 20px' : '40px', width: 'min(420px, 90vw)', boxShadow: '0 25px 60px rgba(0,0,0,0.35)', position: 'relative' }}>
            <button onClick={() => { setShowTrialModal(false); setTrialSuccess(false); }} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: t.textFaint, lineHeight: 1 }}>✕</button>

            {trialSuccess ? (
              <div style={{ textAlign: 'center', paddingTop: '8px' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(79,70,229,0.1)', border: '1px solid rgba(79,70,229,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>✦</div>
                <h3 style={{ color: t.text, fontSize: '22px', fontWeight: 700, margin: '0 0 10px' }}>{L.trialModal.successTitle}</h3>
                <p style={{ color: t.textMuted, fontSize: '14px', margin: '0 0 6px', lineHeight: 1.6 }}>
                  {L.trialModal.successBody}
                </p>
                <p style={{ color: t.textFaint, fontSize: '12px', margin: '0 0 28px' }}>{L.trialModal.successSpam}</p>
                <button onClick={() => { setShowTrialModal(false); setShowLogin(true); setTrialSuccess(false); }}
                  style={{ width: '100%', padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 20px rgba(79,70,229,0.3)' }}>
                  {L.trialModal.successCta}
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 22, color: '#818cf8' }}>✦</span>
                    <h2 style={{ color: t.text, fontSize: '21px', fontWeight: 700, margin: 0 }}>{L.trialModal.title}</h2>
                  </div>
                  <p style={{ color: t.textMuted, fontSize: '13px', margin: 0 }}>{L.trialModal.subtitle}</p>
                </div>
                <form onSubmit={handleStartTrial}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>{L.trialModal.orgLabel}</label>
                    <input value={trialOrgName} onChange={e => setTrialOrgName(e.target.value)} placeholder={L.trialModal.orgNamePlaceholder} required style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#4f46e5'} onBlur={e => e.target.style.borderColor = t.inputBorder} />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>{L.trialModal.emailLabel}</label>
                    <input type="email" value={trialEmail} onChange={e => setTrialEmail(e.target.value)} placeholder="admin@company.com" required style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#4f46e5'} onBlur={e => e.target.style.borderColor = t.inputBorder} />
                  </div>
                  {trialError && (
                    <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 16px', backgroundColor: 'rgba(239,68,68,0.1)', padding: '8px 12px', borderRadius: '8px' }}>
                      {trialError}
                    </p>
                  )}
                  <button type="submit" disabled={trialLoading || !trialEmail.trim() || !trialOrgName.trim()}
                    style={{ width: '100%', padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 20px rgba(79,70,229,0.3)', opacity: (!trialEmail.trim() || !trialOrgName.trim()) ? 0.5 : 1 }}>
                    {trialLoading ? L.trialModal.submitting : L.trialModal.submit}
                  </button>
                  <p style={{ color: t.textFaint, fontSize: '12px', margin: '14px 0 0', textAlign: 'center' }}>
                    {L.trialModal.note}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
